import React, { useState } from 'react';
import { Eye, EyeOff, AlertCircle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { getApiUrl } from '../config/api';
import { theme } from '../config/theme';

type OrganizationOption = {
  name: string;
  slug: string;
};

const Login: React.FC = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [organizationOptions, setOrganizationOptions] = useState<OrganizationOption[]>([]);
  const [organizationSlug, setOrganizationSlug] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const res = await fetch(`${getApiUrl()}/api/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password, organizationSlug: organizationSlug || undefined }),
      });
      const data = await res.json();

      if (data.requiresOrganizationSelection && Array.isArray(data.organizations)) {
        setOrganizationOptions(data.organizations);
        setOrganizationSlug('');
        setError('');
        return;
      }

      if (!res.ok) {
        throw new Error(data.error || 'Erro ao realizar login');
      }

      const user = data.user || null;
      if (!data.token || !user) {
        throw new Error('Resposta de login inválida');
      }

      localStorage.setItem('token', data.token);
      localStorage.setItem('user', JSON.stringify(user));
      navigate('/');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao realizar login');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8 bg-surface dark:bg-surface-dark overflow-y-auto">
        <div className="w-full max-w-md py-8">
          <div className="mb-8">
            <img src="/iasolicitar-logo.svg" alt="IaSolicitar" className="h-14 object-contain" />
          </div>

          <div className="mb-6">
            <h2 className="text-2xl font-bold text-ink-title dark:text-ink-title-dark mb-1">Bem-vindo de volta</h2>
            <p className="text-sm text-ink-subtitle dark:text-ink-subtitle-dark">Faça login para acessar o painel do UltraLicitar</p>
          </div>

          {error && (
            <div className={theme.classes.alert.error + " flex items-start gap-3"}>
              <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-300 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-red-800 dark:text-red-200">{error}</p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-ink-label dark:text-ink-label-dark mb-2">E-mail</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                placeholder="seuemail@empresa.com"
                className={theme.classes.input.base}
              />
            </div>

            {organizationOptions.length > 0 && (
              <div>
                <label className="block text-sm font-medium text-ink-label dark:text-ink-label-dark mb-2">Empresa</label>
                <select
                  value={organizationSlug}
                  onChange={(event) => setOrganizationSlug(event.target.value)}
                  required
                  className={theme.classes.input.base}
                >
                  <option value="">Selecione a empresa</option>
                  {organizationOptions.map((organization) => (
                    <option key={organization.slug} value={organization.slug}>
                      {organization.name}
                    </option>
                  ))}
                </select>
                <p className="mt-2 text-xs text-ink-subtitle dark:text-ink-subtitle-dark">
                  Este e-mail possui acesso a mais de uma empresa.
                </p>
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-ink-label dark:text-ink-label-dark mb-2">Senha</label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  placeholder="Digite sua senha"
                  className={theme.classes.input.base + " pr-12"}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-ink-placeholder dark:text-ink-placeholder-dark hover:text-ink-subtitle dark:text-ink-subtitle-dark transition-colors"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className={theme.classes.button.primary + " w-full py-3.5"}
            >
              {loading ? 'Entrando...' : organizationOptions.length > 0 ? 'Entrar na empresa selecionada' : 'Entrar'}
            </button>
          </form>

          <div className="mt-6 text-sm text-ink-subtitle dark:text-ink-subtitle-dark space-y-3">
            <p className="text-center">
              <button
                onClick={() => navigate('/forgot-password')}
                className={theme.classes.link.primary}
              >
                Esqueci minha senha
              </button>
            </p>
            <p className="text-center">
              Ainda não tem empresa cadastrada?{' '}
              <button
                onClick={() => navigate('/register')}
                className={theme.classes.link.primary}
              >
                Criar empresa
              </button>
            </p>
          </div>
        </div>
      </div>

      <div className={`hidden lg:flex lg:w-1/2 bg-gradient-to-br ${theme.colors.gradients.backgroundDark} relative overflow-hidden`}>
        <div className="absolute inset-0 opacity-10">
          <svg className="w-full h-full" xmlns="http://www.w3.org/2000/svg">
            <defs>
              <pattern id="diagonal-lines-2" x="0" y="0" width="40" height="40" patternUnits="userSpaceOnUse">
                <line x1="0" y1="40" x2="40" y2="0" stroke="white" strokeWidth="1" />
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#diagonal-lines-2)" />
          </svg>
        </div>

        <div className="relative z-10 flex items-center justify-center w-full p-16">
          <div className="text-center text-white">
            <h2 className="text-5xl font-bold mb-6">Bem-vindo ao UltraLicitar</h2>
            <p className="text-xl opacity-90 mb-8">A plataforma completa para gestão de licitações</p>
            <div className="grid grid-cols-1 gap-6 max-w-md mx-auto text-left">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center flex-shrink-0">
                  <svg className="w-6 h-6 text-white" viewBox="0 0 24 24" fill="none">
                    <path d="M5 12l4 4L19 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </div>
                <div>
                  <h3 className="font-semibold mb-1">Gestão Completa</h3>
                  <p className="text-sm opacity-80">Licitações, contratos e notificações em um só lugar</p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center flex-shrink-0">
                  <svg className="w-6 h-6 text-white" viewBox="0 0 24 24" fill="none">
                    <path d="M3 12h18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                  </svg>
                </div>
                <div>
                  <h3 className="font-semibold mb-1">IA Integrada</h3>
                  <p className="text-sm opacity-80">Análises inteligentes dos editais</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
